import { authClient } from "@d12labs/auth/client";
import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AuthLayout } from "@/components/pages/auth-layout";

// Better Auth's twoFactorClient plugin redirects here after a sign-in attempt
// returns twoFactorRedirect: true. A temporary cookie set by the server holds
// the pending sign-in; the verifyTotp / verifyBackupCode calls consume it.
//
// If a user lands here without a pending challenge (e.g. typed the URL or
// session expired), the verify calls will error and we point them back to
// /login.
export function TwoFactorVerifyPage() {
	const navigate = useNavigate();
	const [mode, setMode] = useState<"totp" | "backup">("totp");
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = code.trim();
		if (trimmed.length === 0) return;
		setError(null);
		setSubmitting(true);
		const { error: err } =
			mode === "totp"
				? await authClient.twoFactor.verifyTotp({ code: trimmed })
				: await authClient.twoFactor.verifyBackupCode({ code: trimmed });
		setSubmitting(false);
		if (err) {
			setError(err.message ?? "Code didn't verify");
			return;
		}
		await navigate({ to: "/home" });
	};

	const switchMode = () => {
		setMode(mode === "totp" ? "backup" : "totp");
		setCode("");
		setError(null);
	};

	return (
		<AuthLayout
			title="Two-factor required"
			description={
				mode === "totp"
					? "Enter the 6-digit code from your authenticator app."
					: "Enter one of your saved backup codes. Each one works once."
			}
			footer={
				<Link to="/login" className="underline">
					Use a different account
				</Link>
			}
		>
			<form onSubmit={handleSubmit} className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<Label htmlFor="tfa-code">{mode === "totp" ? "Authenticator code" : "Backup code"}</Label>
					<Input
						id="tfa-code"
						value={code}
						onChange={(e) => setCode(e.target.value)}
						inputMode={mode === "totp" ? "numeric" : "text"}
						autoComplete="one-time-code"
						placeholder={mode === "totp" ? "123456" : "xxxxxxxx"}
						autoFocus
						required
					/>
				</div>
				{error && <p className="text-sm text-destructive">{error}</p>}
				<Button type="submit" disabled={submitting || code.trim().length === 0}>
					{submitting ? "Verifying…" : "Verify"}
				</Button>
				<button
					type="button"
					onClick={switchMode}
					className="text-xs text-muted-foreground hover:text-foreground underline"
				>
					{mode === "totp" ? "Use a backup code instead" : "Use your authenticator code"}
				</button>
			</form>
		</AuthLayout>
	);
}
